/**
 * THE ORDER PICKER ON "RECEIVE MATERIAL" — 02-11-2026.
 *
 * Was a drop-down reading "OVPO-20261031-165 · VENDOR · 5000 pending". A
 * store-man with a challan in his hand could not tell from that which order
 * the lorry belonged to, so he received it as ad-hoc and Procurement linked it
 * by hand afterwards. Now each open order for the site is a card: who it is
 * from, what is still to come (material and quantity), when it is due, and
 * how much has arrived. A search box matches vendor, material or order number,
 * which is what is written on the challan.
 *
 * Quantities only, never money: the gate needs to match a lorry, and prices
 * do not belong on a gate screen (decided 02-11-2026).
 *
 * Orders approved or sent but not yet marked "Vendor acknowledged" are shown
 * greyed out, not selectable. The rule that only an acknowledged order can be
 * received is unchanged; the card tells him to ring Procurement instead of
 * falling back to ad-hoc.
 */
import { useMemo, useState } from 'react'
import { CalendarClock, Check, PackageSearch, Phone, Repeat, Search, Truck } from 'lucide-react'
import { Loading } from '@/components/ui'
import { ddmmyyyy, qty } from '@/lib/format'
import type { Material } from '@/lib/types'

export const RECEIVABLE = ['VENDACK', 'PARTIALLY_RECEIVED']
export const WAITING = ['APPROVED', 'VENDIN']

export interface PickerPo {
  id: string
  vendor_id: string
  status: string
  date_created: string
  delivery_date: string | null
  lines: { material_id: string; ordered: number; received: number; pending: number }[]
}

export function isReceivable(status: string) { return RECEIVABLE.includes(status) }

function dueBadge(delivery: string | null) {
  if (!delivery) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const d = new Date(`${delivery.slice(0, 10)}T00:00:00`)
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000)
  if (days < 0) return { text: `Overdue ${-days} day${days === -1 ? '' : 's'}`, cls: 'pill-red' }
  if (days === 0) return { text: 'Due today', cls: 'pill-amber' }
  if (days <= 7) return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, cls: 'pill-blue' }
  return { text: `Due ${ddmmyyyy(delivery)}`, cls: 'pill-grey' }
}

export function PoPicker({ pos, byId, vendorName, onPick }: {
  pos: PickerPo[] | null
  byId: Record<string, Material>
  vendorName: (id: string) => string
  onPick: (poId: string) => void
}) {
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<'DUE' | 'VENDOR' | 'NEWEST'>('DUE')

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase()
    const list = (pos ?? []).filter((p) => {
      if (!t) return true
      const hay = [p.id, vendorName(p.vendor_id),
        ...p.lines.map((l) => `${byId[l.material_id]?.item_name ?? ''} ${byId[l.material_id]?.item_code ?? ''}`)]
        .join(' ').toLowerCase()
      return t.split(/\s+/).every((w) => hay.includes(w))
    })
    const due = (p: PickerPo) => p.delivery_date ?? '9999-12-31'
    list.sort((a, b) => {
      // Receivable first, always: a greyed card is information, not a choice.
      const r = Number(isReceivable(b.status)) - Number(isReceivable(a.status))
      if (r !== 0) return r
      if (sort === 'VENDOR') return vendorName(a.vendor_id).localeCompare(vendorName(b.vendor_id))
      if (sort === 'NEWEST') return b.date_created.localeCompare(a.date_created)
      return due(a).localeCompare(due(b))
    })
    return list
  }, [pos, q, sort, byId, vendorName])

  if (pos === null) return <Loading label="Finding this site's open orders…" />

  const receivable = pos.filter((p) => isReceivable(p.status)).length

  if (pos.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
        <PackageSearch className="mx-auto h-8 w-8 text-slate-400" />
        <p className="mt-2 text-sm font-medium text-slate-700">No open purchase order for this site</p>
        <p className="mt-1 text-xs text-slate-500">
          If the challan quotes a purchase order number, ring Procurement before receiving it as ad-hoc.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className="field pl-9" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search vendor, material or PO number - as on the challan" />
        </div>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs" role="tablist" aria-label="Sort orders">
          {([['DUE', 'Due first'], ['VENDOR', 'Vendor'], ['NEWEST', 'Newest']] as const).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={sort === k} onClick={() => setSort(k)}
                    className={`rounded-md px-2.5 py-1.5 font-medium transition-colors
                                ${sort === k ? 'bg-oval-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        {receivable} order{receivable === 1 ? '' : 's'} ready to receive
        {pos.length > receivable && `, ${pos.length - receivable} waiting for the vendor's acknowledgement`}.
        Tap the one this lorry belongs to.
      </p>

      {shown.length === 0 ? (
        <p className="rounded-lg bg-white px-4 py-6 text-center text-sm text-slate-500">
          Nothing matches &ldquo;{q}&rdquo;. Try the vendor&apos;s name or one word of the material.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((p) => <PoCard key={p.id} po={p} byId={byId} vendorName={vendorName} onPick={onPick} />)}
        </div>
      )}
    </div>
  )
}

function PoCard({ po, byId, vendorName, onPick }: {
  po: PickerPo
  byId: Record<string, Material>
  vendorName: (id: string) => string
  onPick: (poId: string) => void
}) {
  const ok = isReceivable(po.status)
  const pending = po.lines.filter((l) => l.pending > 0.0001)
  // Each line's own share, averaged: bags and kilos cannot be added together.
  const shares = po.lines.filter((l) => l.ordered > 0).map((l) => Math.min(1, l.received / l.ordered))
  const pct = shares.length ? Math.round((shares.reduce((a, x) => a + x, 0) / shares.length) * 100) : 0
  const due = dueBadge(po.delivery_date)

  return (
    <button type="button" disabled={!ok} onClick={() => onPick(po.id)}
            className={`group flex flex-col rounded-xl border bg-white p-4 text-left shadow-sm transition
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-oval-500
                        ${ok ? 'border-slate-200 hover:-translate-y-0.5 hover:border-oval-400 hover:shadow-md'
                             : 'cursor-not-allowed border-dashed border-slate-300 bg-slate-50 opacity-75'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="line-clamp-2 break-words text-base font-semibold leading-snug text-slate-800" title={vendorName(po.vendor_id)}>
            {vendorName(po.vendor_id)}
          </p>
          <p className="font-mono text-xs text-slate-500">{po.id} · {ddmmyyyy(po.date_created)}</p>
        </div>
        {ok
          ? <span className={po.status === 'PARTIALLY_RECEIVED' ? 'pill-blue' : 'pill-green'}>
              {po.status === 'PARTIALLY_RECEIVED' ? 'Partly received' : 'Acknowledged'}
            </span>
          : <span className="pill-grey">Not acknowledged</span>}
      </div>

      <ul className="mt-3 space-y-1.5">
        {pending.slice(0, 3).map((l) => (
          <li key={l.material_id} className="flex items-baseline justify-between gap-2 text-sm">
            <span className="min-w-0 truncate text-slate-700" title={byId[l.material_id]?.item_name}>
              {byId[l.material_id]?.item_name ?? l.material_id}
            </span>
            <span className="shrink-0 font-semibold tabular-nums text-slate-800">
              {qty(l.pending)} <span className="text-xs font-normal text-slate-500">{byId[l.material_id]?.unit_of_measure ?? ''}</span>
            </span>
          </li>
        ))}
        {pending.length > 3 && (
          <li className="text-xs text-slate-500">+{pending.length - 3} more material{pending.length - 3 === 1 ? '' : 's'}</li>
        )}
      </ul>
      <p className="mt-1 text-[11px] text-slate-400">still to come</p>

      <div className="mt-3">
        <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1 text-[11px] text-slate-500">{pct}% received so far</p>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        {due && <span className={due.cls}><CalendarClock className="h-3 w-3" />{due.text}</span>}
        {ok ? (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-oval-700 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100 max-sm:opacity-100">
            <Truck className="h-3.5 w-3.5" /> Receive against this
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-800">
            <Phone className="h-3 w-3" /> Waiting for Procurement to mark it acknowledged - ring them
          </span>
        )}
      </div>
    </button>
  )
}

/** The chosen order, collapsed, with the way back to the list. */
export function PickedPo({ po, poId, byId, vendorName, onChange, locked }: {
  po: PickerPo | undefined
  poId: string
  byId: Record<string, Material>
  vendorName: (id: string) => string
  onChange: () => void
  locked?: boolean
}) {
  const pending = (po?.lines ?? []).filter((l) => l.pending > 0.0001)
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
        <Check className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-teal-900">
          Receiving against {poId}{po ? ` · ${vendorName(po.vendor_id)}` : ''}
        </p>
        <p className="truncate text-xs text-teal-800">
          {pending.map((l) => `${byId[l.material_id]?.item_name ?? l.material_id} ${qty(l.pending)} ${byId[l.material_id]?.unit_of_measure ?? ''}`).join(' · ')}
        </p>
      </div>
      {!locked && (
        <button type="button" className="btn-secondary btn-sm" onClick={onChange}>
          <Repeat className="h-4 w-4" /> Change order
        </button>
      )}
    </div>
  )
}
